// HTTP proxy session hook — manages lifecycle for proxied device web UI access.
//
// Provides open/close mutations and proxy URL generation for the iframe.

import { useState, useCallback, useRef } from 'react'
import axios from 'axios'
import { config } from '../config'

export type ProxyConnectionState = 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected'

export interface UseHttpProxySessionReturn {
  sessionId: string | null
  connectionState: ProxyConnectionState
  proxyUrl: (path?: string) => string
  /**
   * Open a browsing session.
   *
   * Passing a `consentId` opens it with recording armed from the first
   * request. That is why enabling recording reopens the session rather
   * than toggling the live one: the login exchange is the most valuable
   * thing in a capture, and a recording that starts after login has
   * already missed it.
   */
  open: (consentId?: string) => Promise<void>
  close: () => Promise<void>
}

export function useHttpProxySession(deviceId: string): UseHttpProxySessionReturn {
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [connectionState, setConnectionState] = useState<ProxyConnectionState>('idle')
  const sessionIdRef = useRef<string | null>(null)
  const initialPathRef = useRef<string>('/')


  const open = useCallback(async (consentId?: string) => {
    setConnectionState('connecting')
    try {
      const response = await axios.post<{ sessionId: string; initialPath?: string }>(
        `${config.backendApi}/api/devices/${deviceId}/web-proxy/open`,
        consentId ? { consentId } : {},
        { withCredentials: true }
      )
      console.log('Web proxy open response:', response.data)
      setSessionId(response.data.sessionId)
      sessionIdRef.current = response.data.sessionId
      // Store initial path for devices that need a specific post-login URL
      initialPathRef.current = response.data.initialPath || '/'
      console.log('Initial path set to:', initialPathRef.current)
      setConnectionState('connected')
    } catch {
      setConnectionState('error')
      throw new Error('Failed to open proxy session')
    }
  }, [deviceId])

  const close = useCallback(async () => {
    const sid = sessionIdRef.current
    if (!sid) return
    try {
      await axios.post(
        `${config.backendApi}/api/devices/${deviceId}/web-proxy/close`,
        {},
        {
          withCredentials: true,
          headers: { 'X-Proxy-Session-Id': sid },
        }
      )
    } finally {
      setSessionId(null)
      sessionIdRef.current = null
      setConnectionState('disconnected')
    }
  }, [deviceId])

  const proxyUrl = useCallback((path?: string) => {
    const sid = sessionIdRef.current
    if (!sid) return ''
    const p = path ?? initialPathRef.current
    // Strip leading slash and add `_root_` placeholder when the path
    // is empty. Backend's wildcard route `/{*path}` doesn't match the
    // zero-segment case, and Loco's `.add("/", …)` registers the
    // route without a trailing slash — so an iframe src that ends
    // with `…/s/<sid>/` returns 404. Inserting an explicit
    // `_root_` segment makes the wildcard always match, and the
    // backend's proxy handler translates `_root_` → device root `/`.
    const cleanPath = p.replace(/^\//, '') || '_root_'
    return `${config.backendApi}/api/devices/${deviceId}/web-proxy/s/${sid}/${cleanPath}`
  }, [deviceId])

  return {
    sessionId,
    connectionState,
    proxyUrl,
    open,
    close,
  }
}
